/**
 * FoodIcons.ts
 * Handcrafted, multi-layered illustrated inline SVG iconography suite
 * for all 15 regional US specialty dishes in American Dream: Pocket Life.
 *
 * Each icon features:
 * - Bespoke 64x64 vector canvas
 * - Plating, boards, or glassware bases
 * - Authentic regional ingredients, textures, and toppings
 * - Multi-stop linear and radial gradients with unique prefix IDs
 * - Specular highlights, steam curls, and freshness accents
 */

export const FOOD_SVG_ICONS: Record<string, string> = {
  /**
   * 1. NY Slice Pizza (food_ny_slice)
   * Triangular classic NY fold slice with blistered toasted crust rim, rich marinara sauce,
   * gooey mozzarella folds, pepperoni discs with curled glossy edges, oregano flecks, and steam.
   */
  food_ny_slice: `<svg viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
    <defs>
      <linearGradient id="ny_sl_plate" x1="8" y1="12" x2="56" y2="60" gradientUnits="userSpaceOnUse">
        <stop offset="0%" stop-color="#334155"/>
        <stop offset="100%" stop-color="#0f172a"/>
      </linearGradient>
      <linearGradient id="ny_sl_crust" x1="14" y1="10" x2="52" y2="18" gradientUnits="userSpaceOnUse">
        <stop offset="0%" stop-color="#f59e0b"/>
        <stop offset="50%" stop-color="#d97706"/>
        <stop offset="100%" stop-color="#92400e"/>
      </linearGradient>
      <linearGradient id="ny_sl_cheese" x1="32" y1="16" x2="32" y2="56" gradientUnits="userSpaceOnUse">
        <stop offset="0%" stop-color="#fef08a"/>
        <stop offset="40%" stop-color="#facc15"/>
        <stop offset="100%" stop-color="#f59e0b"/>
      </linearGradient>
      <radialGradient id="ny_sl_pep1" cx="30" cy="28" r="8" gradientUnits="userSpaceOnUse">
        <stop offset="0%" stop-color="#ef4444"/>
        <stop offset="70%" stop-color="#b91c1c"/>
        <stop offset="100%" stop-color="#7f1d1d"/>
      </radialGradient>
      <radialGradient id="ny_sl_pep2" cx="38" cy="38" r="7" gradientUnits="userSpaceOnUse">
        <stop offset="0%" stop-color="#ef4444"/>
        <stop offset="70%" stop-color="#b91c1c"/>
        <stop offset="100%" stop-color="#7f1d1d"/>
      </radialGradient>
      <radialGradient id="ny_sl_pep3" cx="24" cy="40" r="7" gradientUnits="userSpaceOnUse">
        <stop offset="0%" stop-color="#ef4444"/>
        <stop offset="70%" stop-color="#b91c1c"/>
        <stop offset="100%" stop-color="#7f1d1d"/>
      </radialGradient>
    </defs>
    <!-- Base Plate Shadow -->
    <ellipse cx="32" cy="54" rx="26" ry="7" fill="rgba(0,0,0,0.35)"/>
    <ellipse cx="32" cy="52" rx="25" ry="6" fill="url(#ny_sl_plate)" stroke="rgba(255,255,255,0.12)" stroke-width="1"/>
    <!-- Crust Rim -->
    <path d="M12 18 C 22 10, 42 10, 52 18 C 50 22, 14 22, 12 18 Z" fill="url(#ny_sl_crust)" stroke="#78350f" stroke-width="1.2"/>
    <circle cx="22" cy="14" r="1.5" fill="#78350f"/>
    <circle cx="34" cy="13" r="1.8" fill="#5c2607"/>
    <circle cx="44" cy="15" r="1.4" fill="#78350f"/>
    <!-- Sauce & Cheese Body -->
    <path d="M14 19 Q 32 17 50 19 L 34 56 C 33 58, 31 58, 30 56 Z" fill="#dc2626"/>
    <path d="M15 21 Q 32 19 49 21 L 33 55 C 32 56, 31 56, 31 55 Z" fill="url(#ny_sl_cheese)"/>
    <!-- Melted Cheese Pools -->
    <path d="M22 28 Q 28 32 35 27 Q 41 33 46 29" stroke="#fef9c3" stroke-width="2.5" stroke-linecap="round"/>
    <path d="M26 36 Q 32 40 38 35" stroke="#fef08a" stroke-width="2" stroke-linecap="round"/>
    <!-- Pepperoni Discs -->
    <ellipse cx="30" cy="28" rx="6" ry="4.5" fill="url(#ny_sl_pep1)" stroke="#991b1b" stroke-width="0.8"/>
    <ellipse cx="29" cy="27" rx="3.5" ry="2" fill="none" stroke="#fca5a5" stroke-width="0.7"/>
    <ellipse cx="38" cy="38" rx="5.5" ry="4" fill="url(#ny_sl_pep2)" stroke="#991b1b" stroke-width="0.8"/>
    <ellipse cx="37" cy="37" rx="3" ry="1.8" fill="none" stroke="#fca5a5" stroke-width="0.6"/>
    <ellipse cx="24" cy="40" rx="5" ry="3.8" fill="url(#ny_sl_pep3)" stroke="#991b1b" stroke-width="0.8"/>
    <ellipse cx="23" cy="39" rx="2.8" ry="1.6" fill="none" stroke="#fca5a5" stroke-width="0.6"/>
    <!-- Herb Flecks (Oregano) -->
    <circle cx="21" cy="24" r="0.8" fill="#15803d"/>
    <circle cx="37" cy="24" r="0.9" fill="#166534"/>
    <circle cx="31" cy="46" r="0.8" fill="#15803d"/>
    <circle cx="34" cy="48" r="0.7" fill="#166534"/>
    <!-- Rising Steam Accent -->
    <path d="M26 12 C 24 8, 28 6, 26 2" stroke="rgba(255,255,255,0.4)" stroke-width="1.2" stroke-linecap="round" fill="none"/>
    <path d="M38 12 C 36 7, 40 5, 38 1" stroke="rgba(255,255,255,0.45)" stroke-width="1.2" stroke-linecap="round" fill="none"/>
  </svg>`,

  /**
   * 2. Everything Bagel & Schmear (food_ny_bagel)
   * Golden-brown bagel halved horizontally, textured crumb, thick wavy swirl of cream cheese
   * schmear, sprinkled black poppy seeds, toasted sesame seeds, and golden garlic flakes.
   */
  food_ny_bagel: `<svg viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
    <defs>
      <linearGradient id="ny_bg_crust" x1="8" y1="16" x2="56" y2="52" gradientUnits="userSpaceOnUse">
        <stop offset="0%" stop-color="#f59e0b"/>
        <stop offset="45%" stop-color="#d97706"/>
        <stop offset="100%" stop-color="#92400e"/>
      </linearGradient>
      <linearGradient id="ny_bg_schmear" x1="16" y1="20" x2="48" y2="44" gradientUnits="userSpaceOnUse">
        <stop offset="0%" stop-color="#ffffff"/>
        <stop offset="70%" stop-color="#f8fafc"/>
        <stop offset="100%" stop-color="#e2e8f0"/>
      </linearGradient>
      <radialGradient id="ny_bg_hole" cx="32" cy="34" r="10" gradientUnits="userSpaceOnUse">
        <stop offset="0%" stop-color="#0f172a"/>
        <stop offset="100%" stop-color="#1e293b"/>
      </radialGradient>
    </defs>
    <!-- Plate / Paper Wrapper Shadow -->
    <ellipse cx="32" cy="52" rx="27" ry="8" fill="rgba(0,0,0,0.3)"/>
    <ellipse cx="32" cy="50" rx="26" ry="7" fill="#1e293b" stroke="rgba(255,255,255,0.1)" stroke-width="1"/>
    <!-- Bagel Bottom Base -->
    <path d="M8 36 C 8 22, 56 22, 56 36 C 56 50, 8 50, 8 36 Z" fill="url(#ny_bg_crust)" stroke="#78350f" stroke-width="1.2"/>
    <!-- Cream Cheese Schmear (Generous swirl) -->
    <path d="M11 34 C 13 25, 51 25, 53 34 C 51 44, 13 44, 11 34 Z" fill="url(#ny_bg_schmear)"/>
    <path d="M14 33 Q 22 28 32 30 Q 42 27 50 33 Q 44 40 32 38 Q 20 40 14 33 Z" fill="#ffffff"/>
    <path d="M17 32 Q 25 35 34 32 Q 42 36 47 32" stroke="#cbd5e1" stroke-width="1.5" stroke-linecap="round" fill="none"/>
    <!-- Bagel Central Hole -->
    <ellipse cx="32" cy="34" rx="9" ry="6" fill="url(#ny_bg_hole)" stroke="#b45309" stroke-width="1.5"/>
    <!-- Everything Seasoning (Poppy, Sesame, Minced Garlic) -->
    <!-- Black Poppy Seeds -->
    <circle cx="18" cy="27" r="1" fill="#0f172a"/>
    <circle cx="24" cy="25" r="1.1" fill="#0f172a"/>
    <circle cx="39" cy="26" r="1.2" fill="#0f172a"/>
    <circle cx="46" cy="28" r="1" fill="#0f172a"/>
    <circle cx="20" cy="40" r="1" fill="#0f172a"/>
    <circle cx="44" cy="41" r="1.1" fill="#0f172a"/>
    <!-- Toasted Sesame Seeds -->
    <ellipse cx="22" cy="29" rx="1.5" ry="0.8" transform="rotate(30 22 29)" fill="#fef08a" stroke="#d97706" stroke-width="0.4"/>
    <ellipse cx="42" cy="28" rx="1.5" ry="0.8" transform="rotate(-25 42 28)" fill="#fef08a" stroke="#d97706" stroke-width="0.4"/>
    <ellipse cx="28" cy="24" rx="1.5" ry="0.8" transform="rotate(15 28 24)" fill="#fef08a" stroke="#d97706" stroke-width="0.4"/>
    <ellipse cx="36" cy="23" rx="1.5" ry="0.8" transform="rotate(-40 36 23)" fill="#fef08a" stroke="#d97706" stroke-width="0.4"/>
    <ellipse cx="26" cy="41" rx="1.5" ry="0.8" transform="rotate(45 26 41)" fill="#fef08a" stroke="#d97706" stroke-width="0.4"/>
    <ellipse cx="38" cy="42" rx="1.5" ry="0.8" transform="rotate(-15 38 42)" fill="#fef08a" stroke="#d97706" stroke-width="0.4"/>
    <!-- Garlic Flecks -->
    <rect x="30" y="22" width="2" height="1.8" rx="0.5" fill="#fde68a" stroke="#b45309" stroke-width="0.4"/>
    <rect x="15" y="36" width="2.2" height="1.8" rx="0.5" fill="#fde68a" stroke="#b45309" stroke-width="0.4"/>
    <rect x="48" y="35" width="2" height="2" rx="0.5" fill="#fde68a" stroke="#b45309" stroke-width="0.4"/>
  </svg>`,

  /**
   * 3. Classic NY Cheesecake (food_ny_cheesecake)
   * Tall architectural slice of dense New York cream-cheese cake, warm graham cracker crumb base,
   * glossy deep-ruby strawberry coulis dripping down the side, whole strawberry topper with green calyx.
   */
  food_ny_cheesecake: `<svg viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
    <defs>
      <linearGradient id="ny_cc_plate" x1="6" y1="14" x2="58" y2="58" gradientUnits="userSpaceOnUse">
        <stop offset="0%" stop-color="#475569"/>
        <stop offset="100%" stop-color="#1e293b"/>
      </linearGradient>
      <linearGradient id="ny_cc_top" x1="16" y1="20" x2="48" y2="28" gradientUnits="userSpaceOnUse">
        <stop offset="0%" stop-color="#fef9c3"/>
        <stop offset="60%" stop-color="#fef08a"/>
        <stop offset="100%" stop-color="#fde047"/>
      </linearGradient>
      <linearGradient id="ny_cc_side" x1="10" y1="26" x2="48" y2="48" gradientUnits="userSpaceOnUse">
        <stop offset="0%" stop-color="#fef3c7"/>
        <stop offset="80%" stop-color="#fde68a"/>
        <stop offset="100%" stop-color="#fcd34d"/>
      </linearGradient>
      <linearGradient id="ny_cc_crust" x1="10" y1="46" x2="48" y2="52" gradientUnits="userSpaceOnUse">
        <stop offset="0%" stop-color="#b45309"/>
        <stop offset="100%" stop-color="#78350f"/>
      </linearGradient>
      <radialGradient id="ny_cc_berry" cx="30" cy="15" r="7" gradientUnits="userSpaceOnUse">
        <stop offset="0%" stop-color="#f87171"/>
        <stop offset="60%" stop-color="#dc2626"/>
        <stop offset="100%" stop-color="#991b1b"/>
      </radialGradient>
      <linearGradient id="ny_cc_coulis" x1="28" y1="18" x2="38" y2="42" gradientUnits="userSpaceOnUse">
        <stop offset="0%" stop-color="#ef4444"/>
        <stop offset="100%" stop-color="#b91c1c"/>
      </linearGradient>
    </defs>
    <!-- Dessert Plate -->
    <ellipse cx="32" cy="54" rx="27" ry="7" fill="rgba(0,0,0,0.3)"/>
    <ellipse cx="32" cy="52" rx="25" ry="6" fill="url(#ny_cc_plate)" stroke="rgba(255,255,255,0.15)" stroke-width="1"/>
    <!-- Graham Cracker Crust Base -->
    <path d="M10 46 L 46 36 L 54 44 L 18 53 Z" fill="url(#ny_cc_crust)" stroke="#451a03" stroke-width="0.8"/>
    <!-- Cheesecake Side Face -->
    <path d="M10 26 L 46 16 L 46 36 L 10 46 Z" fill="url(#ny_cc_side)" stroke="#fde68a" stroke-width="0.6"/>
    <!-- Cheesecake Top Wedge -->
    <path d="M10 26 L 46 16 L 54 24 L 18 33 Z" fill="url(#ny_cc_top)" stroke="#fef08a" stroke-width="0.8"/>
    <!-- Cheesecake Front Cut Edge -->
    <path d="M18 33 L 54 24 L 54 44 L 18 53 Z" fill="#fef9c3" stroke="#fef08a" stroke-width="0.6"/>
    <!-- Dripping Strawberry Coulis -->
    <path d="M26 23 Q 32 21 40 23 Q 44 26 42 30 Q 40 34 44 38 L 44 42 Q 40 40 38 34 Q 35 32 30 35 Q 26 31 26 23 Z" fill="url(#ny_cc_coulis)"/>
    <path d="M34 35 Q 36 41 35 45 Q 33 46 33 42 Z" fill="#b91c1c"/>
    <!-- Whole Strawberry Topper -->
    <path d="M32 10 C 26 10, 25 18, 32 23 C 39 18, 38 10, 32 10 Z" fill="url(#ny_cc_berry)" stroke="#7f1d1d" stroke-width="0.6"/>
    <!-- Strawberry Seeds & Highlight -->
    <circle cx="30" cy="14" r="0.6" fill="#fef08a"/>
    <circle cx="34" cy="15" r="0.6" fill="#fef08a"/>
    <circle cx="32" cy="18" r="0.6" fill="#fef08a"/>
    <ellipse cx="29" cy="12" rx="1.5" ry="0.8" fill="rgba(255,255,255,0.6)"/>
    <!-- Green Strawberry Calyx Leaves -->
    <path d="M32 10 L 29 6 L 31 9 L 34 6 L 33 9 L 36 8 L 33 11 Z" fill="#22c55e" stroke="#15803d" stroke-width="0.5"/>
  </svg>`,

  /**
   * 4. In-N-Out Double Burger (food_ca_double_burger)
   * Plump golden brioche bun top with scattered sesame seeds, two charred seared beef patties,
   * two draped melted cheddar cheese slices, crinkled fresh lettuce, ripe red tomato, and special sauce.
   */
  food_ca_double_burger: `<svg viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
    <defs>
      <linearGradient id="ca_bg_bun" x1="16" y1="10" x2="48" y2="28" gradientUnits="userSpaceOnUse">
        <stop offset="0%" stop-color="#fbbf24"/>
        <stop offset="60%" stop-color="#f59e0b"/>
        <stop offset="100%" stop-color="#b45309"/>
      </linearGradient>
      <linearGradient id="ca_bg_patty" x1="12" y1="30" x2="52" y2="44" gradientUnits="userSpaceOnUse">
        <stop offset="0%" stop-color="#78350f"/>
        <stop offset="50%" stop-color="#451a03"/>
        <stop offset="100%" stop-color="#291002"/>
      </linearGradient>
      <linearGradient id="ca_bg_cheese" x1="12" y1="32" x2="52" y2="42" gradientUnits="userSpaceOnUse">
        <stop offset="0%" stop-color="#fde047"/>
        <stop offset="100%" stop-color="#f59e0b"/>
      </linearGradient>
    </defs>
    <!-- Burger Plate Shadow -->
    <ellipse cx="32" cy="56" rx="26" ry="6" fill="rgba(0,0,0,0.35)"/>
    <!-- Bottom Bun -->
    <path d="M14 48 C 14 44, 50 44, 50 48 C 50 54, 14 54, 14 48 Z" fill="#d97706" stroke="#92400e" stroke-width="1"/>
    <!-- Bottom Beef Patty -->
    <rect x="12" y="42" width="40" height="7" rx="3.5" fill="url(#ca_bg_patty)" stroke="#291002" stroke-width="0.8"/>
    <!-- Melted Cheddar Layer 2 (draping corners) -->
    <path d="M12 43 L 20 47 L 28 42 L 38 48 L 46 43 L 52 46 L 50 41 L 14 41 Z" fill="url(#ca_bg_cheese)"/>
    <!-- Top Beef Patty -->
    <rect x="12" y="34" width="40" height="7" rx="3.5" fill="url(#ca_bg_patty)" stroke="#291002" stroke-width="0.8"/>
    <!-- Melted Cheddar Layer 1 -->
    <path d="M14 36 L 24 41 L 34 36 L 44 41 L 50 35 L 14 35 Z" fill="url(#ca_bg_cheese)"/>
    <!-- Fresh Tomato Slice -->
    <rect x="14" y="29" width="36" height="5" rx="2.5" fill="#ef4444" stroke="#b91c1c" stroke-width="0.8"/>
    <!-- Crinkled Green Leaf Lettuce -->
    <path d="M10 28 Q 16 25 22 29 Q 28 25 34 29 Q 40 25 46 29 Q 52 26 54 28 L 52 31 Q 44 32 36 30 Q 28 32 20 30 Q 14 32 10 31 Z" fill="#22c55e" stroke="#15803d" stroke-width="0.8"/>
    <!-- Brioche Bun Top -->
    <path d="M12 26 C 12 12, 52 12, 52 26 C 52 28, 12 28, 12 26 Z" fill="url(#ca_bg_bun)" stroke="#92400e" stroke-width="1.2"/>
    <!-- Bun Gloss Highlight -->
    <path d="M20 18 Q 32 14 44 18" stroke="rgba(255,255,255,0.4)" stroke-width="2" stroke-linecap="round"/>
    <!-- White Sesame Seeds -->
    <ellipse cx="22" cy="18" rx="1.5" ry="0.9" fill="#fef08a" transform="rotate(-15 22 18)"/>
    <ellipse cx="32" cy="15" rx="1.5" ry="0.9" fill="#fef08a"/>
    <ellipse cx="42" cy="18" rx="1.5" ry="0.9" fill="#fef08a" transform="rotate(20 42 18)"/>
    <ellipse cx="28" cy="21" rx="1.4" ry="0.8" fill="#fef08a" transform="rotate(10 28 21)"/>
    <ellipse cx="38" cy="22" rx="1.4" ry="0.8" fill="#fef08a" transform="rotate(-30 38 22)"/>
  </svg>`,

  /**
   * 5. Sourdough Avocado Toast (food_ca_avocado_toast)
   * Thick rustic sourdough bread slice with dark artisan crust, chunky vibrant guacamole mash
   * with lime sheen, crushed red chili flakes, microgreens, and flaky sea salt.
   */
  food_ca_avocado_toast: `<svg viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
    <defs>
      <linearGradient id="ca_av_bread" x1="12" y1="18" x2="52" y2="48" gradientUnits="userSpaceOnUse">
        <stop offset="0%" stop-color="#fef3c7"/>
        <stop offset="80%" stop-color="#fde68a"/>
        <stop offset="100%" stop-color="#d97706"/>
      </linearGradient>
      <linearGradient id="ca_av_crust" x1="10" y1="16" x2="54" y2="50" gradientUnits="userSpaceOnUse">
        <stop offset="0%" stop-color="#92400e"/>
        <stop offset="100%" stop-color="#451a03"/>
      </linearGradient>
      <linearGradient id="ca_av_mash" x1="16" y1="20" x2="48" y2="44" gradientUnits="userSpaceOnUse">
        <stop offset="0%" stop-color="#84cc16"/>
        <stop offset="60%" stop-color="#65a30d"/>
        <stop offset="100%" stop-color="#4d7c0f"/>
      </linearGradient>
    </defs>
    <!-- Slate Board Base -->
    <rect x="8" y="14" width="48" height="42" rx="6" fill="#1e293b" stroke="rgba(255,255,255,0.12)" stroke-width="1"/>
    <!-- Sourdough Bread Crust -->
    <path d="M14 26 C 14 20, 24 18, 32 18 C 40 18, 50 20, 50 26 L 48 46 C 48 48, 16 48, 16 46 Z" fill="url(#ca_av_crust)"/>
    <!-- Sourdough Crumb Interior -->
    <path d="M16 27 C 16 22, 25 20, 32 20 C 39 20, 48 22, 48 27 L 46 44 C 46 46, 18 46, 18 44 Z" fill="url(#ca_av_bread)"/>
    <!-- Chunky Avocado Mash Layer -->
    <path d="M18 28 C 22 24, 28 27, 34 25 C 40 24, 46 26, 46 30 C 47 36, 45 42, 42 43 C 36 44, 26 44, 20 42 C 17 38, 16 32, 18 28 Z" fill="url(#ca_av_mash)"/>
    <!-- Avocado Chunks / Slices -->
    <path d="M22 30 C 26 27, 30 28, 32 32 C 30 35, 24 35, 22 30 Z" fill="#a3e635"/>
    <path d="M34 28 C 38 27, 42 29, 43 33 C 40 36, 36 35, 34 28 Z" fill="#bef264"/>
    <path d="M24 36 C 28 34, 34 35, 36 38 C 32 41, 26 41, 24 36 Z" fill="#a3e635"/>
    <!-- Red Chili Flakes -->
    <polygon points="26,26 28,25 27,28" fill="#ef4444"/>
    <polygon points="38,26 40,27 39,29" fill="#ef4444"/>
    <polygon points="30,34 32,33 31,36" fill="#dc2626"/>
    <polygon points="42,36 44,35 43,38" fill="#ef4444"/>
    <!-- Delicate Microgreen Sprouts -->
    <path d="M28 32 Q 27 28 24 28 M 27 28 Q 28 26 31 27" stroke="#22c55e" stroke-width="1.2" stroke-linecap="round" fill="none"/>
    <path d="M38 31 Q 39 26 42 27" stroke="#22c55e" stroke-width="1.2" stroke-linecap="round" fill="none"/>
    <!-- Sea Salt Flakes (specular white squares) -->
    <rect x="23" y="32" width="1.2" height="1.2" fill="#ffffff" transform="rotate(45 23 32)"/>
    <rect x="35" y="31" width="1.2" height="1.2" fill="#ffffff" transform="rotate(20 35 31)"/>
    <rect x="29" y="38" width="1.2" height="1.2" fill="#ffffff" transform="rotate(10 29 38)"/>
  </svg>`,

  /**
   * 6. Napa Valley Berry Smoothie (food_ca_napa_smoothie)
   * Tall fluted glass tumbler, layered berry smoothie with rich purple-magenta swirl,
   * whipped froth cap, fresh blackberry & raspberry garnish on rim, striped paper straw.
   */
  food_ca_napa_smoothie: `<svg viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
    <defs>
      <linearGradient id="ca_sm_glass" x1="18" y1="18" x2="46" y2="56" gradientUnits="userSpaceOnUse">
        <stop offset="0%" stop-color="rgba(255,255,255,0.4)"/>
        <stop offset="100%" stop-color="rgba(255,255,255,0.08)"/>
      </linearGradient>
      <linearGradient id="ca_sm_juice" x1="20" y1="22" x2="44" y2="54" gradientUnits="userSpaceOnUse">
        <stop offset="0%" stop-color="#c084fc"/>
        <stop offset="40%" stop-color="#9333ea"/>
        <stop offset="80%" stop-color="#7e22ce"/>
        <stop offset="100%" stop-color="#4c1d95"/>
      </linearGradient>
      <radialGradient id="ca_sm_berry" cx="24" cy="14" r="5" gradientUnits="userSpaceOnUse">
        <stop offset="0%" stop-color="#e879f9"/>
        <stop offset="70%" stop-color="#a21caf"/>
        <stop offset="100%" stop-color="#581c87"/>
      </radialGradient>
    </defs>
    <!-- Base Shadow -->
    <ellipse cx="32" cy="56" rx="18" ry="5" fill="rgba(0,0,0,0.3)"/>
    <!-- Glass Tumbler Back & Liquid Fill -->
    <path d="M20 22 L 24 52 C 24 54, 40 54, 40 52 L 44 22 Z" fill="url(#ca_sm_juice)" stroke="rgba(255,255,255,0.3)" stroke-width="1"/>
    <!-- Smoothie Swirl Texture -->
    <path d="M22 28 Q 32 34 42 28" stroke="#e879f9" stroke-width="2" stroke-linecap="round" fill="none"/>
    <path d="M23 38 Q 32 44 41 38" stroke="#f472b6" stroke-width="2.5" stroke-linecap="round" fill="none"/>
    <path d="M24 46 Q 32 50 40 46" stroke="#c084fc" stroke-width="2" stroke-linecap="round" fill="none"/>
    <!-- Glass Specular Reflection Lines -->
    <path d="M22 24 L 25 50" stroke="rgba(255,255,255,0.5)" stroke-width="1.8" stroke-linecap="round"/>
    <path d="M42 24 L 39 50" stroke="rgba(255,255,255,0.25)" stroke-width="1.2" stroke-linecap="round"/>
    <!-- Whipped Cream / Froth Dome -->
    <path d="M19 22 C 19 16, 26 15, 30 17 C 33 13, 39 14, 41 18 C 45 17, 45 22, 45 22 Z" fill="#ffffff" stroke="#f1f5f9" stroke-width="0.8"/>
    <!-- Striped Paper Straw -->
    <path d="M36 4 L 33 22" stroke="#ef4444" stroke-width="3" stroke-linecap="round"/>
    <path d="M36 7 L 34.5 10 M 35 12 L 34 15 M 34 17 L 33.5 20" stroke="#ffffff" stroke-width="1.8"/>
    <!-- Rim Garnish: Blackberry & Raspberry -->
    <circle cx="24" cy="16" r="3.5" fill="url(#ca_sm_berry)"/>
    <circle cx="22" cy="15" r="1.2" fill="#581c87"/>
    <circle cx="25" cy="14" r="1.2" fill="#581c87"/>
    <circle cx="24" cy="18" r="1.2" fill="#581c87"/>
    <circle cx="28" cy="18" r="2.8" fill="#f43f5e"/>
    <!-- Fresh Mint Leaf -->
    <path d="M20 13 Q 16 11 18 8 Q 22 10 20 13 Z" fill="#22c55e" stroke="#15803d" stroke-width="0.5"/>
  </svg>`,

  /**
   * 7. Smoked Brisket BBQ (food_tx_brisket)
   * Three thick slices of Texas smoked brisket on a dark rustic oak cutting board, dark charred
   * black pepper bark, distinctive pink smoke ring, tender marbled beef fibers, and dripping jus.
   */
  food_tx_brisket: `<svg viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
    <defs>
      <linearGradient id="tx_br_board" x1="6" y1="12" x2="58" y2="54" gradientUnits="userSpaceOnUse">
        <stop offset="0%" stop-color="#78350f"/>
        <stop offset="100%" stop-color="#451a03"/>
      </linearGradient>
      <linearGradient id="tx_br_meat" x1="16" y1="20" x2="48" y2="48" gradientUnits="userSpaceOnUse">
        <stop offset="0%" stop-color="#991b1b"/>
        <stop offset="70%" stop-color="#7f1d1d"/>
        <stop offset="100%" stop-color="#450a0a"/>
      </linearGradient>
    </defs>
    <!-- Heavy Wood Board Base -->
    <rect x="6" y="16" width="52" height="38" rx="5" fill="url(#tx_br_board)" stroke="#291002" stroke-width="1.5"/>
    <rect x="8" y="18" width="48" height="34" rx="4" fill="none" stroke="rgba(255,255,255,0.08)" stroke-width="1"/>
    <!-- Glistening Jus Pool -->
    <ellipse cx="32" cy="42" rx="20" ry="7" fill="rgba(69, 10, 10, 0.6)"/>
    <!-- Brisket Slice 1 (Bottom) -->
    <path d="M14 36 L 46 28 L 50 36 L 18 44 Z" fill="#18181b"/>
    <path d="M16 36 L 44 29 L 48 35 L 20 42 Z" fill="#ef4444"/>
    <path d="M18 36 L 42 30 L 46 34 L 22 40 Z" fill="url(#tx_br_meat)"/>
    <!-- Brisket Slice 2 (Middle) -->
    <path d="M13 30 L 45 22 L 49 30 L 17 38 Z" fill="#18181b"/>
    <path d="M15 30 L 43 23 L 47 29 L 19 36 Z" fill="#ef4444"/>
    <path d="M17 30 L 41 24 L 45 28 L 21 34 Z" fill="url(#tx_br_meat)"/>
    <!-- Brisket Slice 3 (Top - Hero slice) -->
    <path d="M12 24 L 44 16 L 48 24 L 16 32 Z" fill="#09090b"/>
    <!-- Smoke Ring (Crimson / Pink border) -->
    <path d="M14 24 L 42 17 L 46 23 L 18 30 Z" fill="#f87171"/>
    <!-- Interior Marbled Meat -->
    <path d="M16 24 L 40 18 L 44 22 L 20 28 Z" fill="url(#tx_br_meat)"/>
    <!-- Peppery Dark Bark Texture -->
    <circle cx="13" cy="23" r="0.8" fill="#000000"/>
    <circle cx="22" cy="18" r="0.9" fill="#000000"/>
    <circle cx="34" cy="16" r="0.9" fill="#000000"/>
    <circle cx="44" cy="16" r="1.1" fill="#000000"/>
    <!-- Marbled Fat Streaks & Specular Moisture -->
    <path d="M22 23 Q 30 20 38 21" stroke="#fecaca" stroke-width="1.2" stroke-linecap="round"/>
    <path d="M20 26 Q 32 23 40 25" stroke="rgba(255,255,255,0.4)" stroke-width="1" stroke-linecap="round"/>
    <!-- Pickled Jalapeño Slice on Side -->
    <ellipse cx="48" cy="42" rx="4" ry="3" fill="#15803d" stroke="#14532d" stroke-width="0.8"/>
    <ellipse cx="48" cy="42" rx="2" ry="1.5" fill="#4ade80"/>
  </svg>`,

  /**
   * 8. Tex-Mex Street Tacos (food_tx_tacos)
   * Two folded soft corn tortillas with toasted grill char spots, shredded seasoned carne asada,
   * diced white onion cubes, chopped fresh cilantro, and a lime wedge.
   */
  food_tx_tacos: `<svg viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
    <defs>
      <linearGradient id="tx_tc_tortilla" x1="12" y1="20" x2="48" y2="48" gradientUnits="userSpaceOnUse">
        <stop offset="0%" stop-color="#fef08a"/>
        <stop offset="70%" stop-color="#fde047"/>
        <stop offset="100%" stop-color="#ca8a04"/>
      </linearGradient>
    </defs>
    <!-- Plate / Basket Shadow -->
    <ellipse cx="32" cy="54" rx="26" ry="6" fill="rgba(0,0,0,0.3)"/>
    <ellipse cx="32" cy="52" rx="25" ry="5.5" fill="#1e293b" stroke="rgba(255,255,255,0.1)" stroke-width="1"/>
    <!-- Taco 2 (Background Left) -->
    <path d="M12 40 C 10 26, 26 22, 34 26 C 36 34, 30 46, 22 46 Z" fill="url(#tx_tc_tortilla)" stroke="#a16207" stroke-width="1"/>
    <path d="M18 28 Q 24 24 30 27" fill="#7f1d1d"/>
    <!-- Taco 1 (Foreground Right - Main Hero) -->
    <path d="M22 48 C 20 32, 40 24, 52 30 C 54 40, 48 50, 36 50 Z" fill="url(#tx_tc_tortilla)" stroke="#a16207" stroke-width="1.2"/>
    <!-- Char Marks on Corn Tortilla -->
    <ellipse cx="26" cy="42" rx="2" ry="1" fill="#78350f" transform="rotate(20 26 42)"/>
    <ellipse cx="32" cy="47" rx="2.5" ry="1.2" fill="#78350f" transform="rotate(-10 32 47)"/>
    <ellipse cx="44" cy="44" rx="2" ry="1" fill="#78350f" transform="rotate(30 44 44)"/>
    <!-- Seasoned Carne Asada Filling -->
    <path d="M28 34 Q 38 30 48 34 Q 44 42 34 42 Z" fill="#7f1d1d" stroke="#450a0a" stroke-width="0.8"/>
    <!-- Diced White Onion Cubes -->
    <rect x="32" y="33" width="2.4" height="2.4" rx="0.5" fill="#ffffff" stroke="#e2e8f0" stroke-width="0.4"/>
    <rect x="38" y="32" width="2.4" height="2.4" rx="0.5" fill="#ffffff" stroke="#e2e8f0" stroke-width="0.4"/>
    <rect x="42" y="35" width="2.4" height="2.4" rx="0.5" fill="#ffffff" stroke="#e2e8f0" stroke-width="0.4"/>
    <!-- Fresh Cilantro Leaves -->
    <circle cx="35" cy="36" r="1.4" fill="#22c55e"/>
    <circle cx="36" cy="34" r="1.2" fill="#16a34a"/>
    <circle cx="45" cy="38" r="1.4" fill="#22c55e"/>
    <!-- Lime Wedge on the Side -->
    <path d="M14 44 C 14 36, 24 38, 26 46 Z" fill="#84cc16" stroke="#4d7c0f" stroke-width="0.8"/>
    <path d="M16 43 C 16 38, 23 39, 24 45 Z" fill="#bef264"/>
  </svg>`,

  /**
   * 9. Southern Pecan Pie (food_tx_pecan_pie)
   * Fluted golden-brown pastry crust wedge, dark amber caramelized brown sugar molasses filling,
   * glossy toasted pecan halves arranged neatly on top with warm caramel shine.
   */
  food_tx_pecan_pie: `<svg viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
    <defs>
      <linearGradient id="tx_pp_crust" x1="12" y1="16" x2="52" y2="48" gradientUnits="userSpaceOnUse">
        <stop offset="0%" stop-color="#f59e0b"/>
        <stop offset="70%" stop-color="#d97706"/>
        <stop offset="100%" stop-color="#92400e"/>
      </linearGradient>
      <linearGradient id="tx_pp_fill" x1="18" y1="20" x2="48" y2="44" gradientUnits="userSpaceOnUse">
        <stop offset="0%" stop-color="#b45309"/>
        <stop offset="50%" stop-color="#78350f"/>
        <stop offset="100%" stop-color="#451a03"/>
      </linearGradient>
      <radialGradient id="tx_pp_pecan" cx="32" cy="28" r="8" gradientUnits="userSpaceOnUse">
        <stop offset="0%" stop-color="#92400e"/>
        <stop offset="70%" stop-color="#713f12"/>
        <stop offset="100%" stop-color="#422006"/>
      </radialGradient>
    </defs>
    <!-- Pie Plate Shadow -->
    <ellipse cx="32" cy="54" rx="26" ry="6" fill="rgba(0,0,0,0.3)"/>
    <ellipse cx="32" cy="52" rx="25" ry="5.5" fill="#1e293b" stroke="rgba(255,255,255,0.12)" stroke-width="1"/>
    <!-- Fluted Outer Crust Backing -->
    <path d="M12 20 Q 16 16 20 20 Q 24 16 28 20 Q 32 16 36 20 Q 40 16 44 20 Q 48 16 52 20 L 52 26 L 12 26 Z" fill="url(#tx_pp_crust)" stroke="#78350f" stroke-width="0.8"/>
    <!-- Pie Wedge Base & Sides -->
    <path d="M12 24 L 52 24 L 34 54 C 33 55, 31 55, 30 54 Z" fill="url(#tx_pp_crust)" stroke="#78350f" stroke-width="1"/>
    <!-- Amber Molasses Caramel Filling -->
    <path d="M14 25 L 50 25 L 33 52 C 32 53, 31 53, 31 52 Z" fill="url(#tx_pp_fill)"/>
    <!-- Toasted Pecan Halves with Gloss -->
    <!-- Pecan 1 (Top Left) -->
    <ellipse cx="24" cy="29" rx="5" ry="3" fill="url(#tx_pp_pecan)" stroke="#291002" stroke-width="0.8" transform="rotate(-20 24 29)"/>
    <path d="M20 29 Q 24 28 28 29" stroke="#b45309" stroke-width="0.8"/>
    <!-- Pecan 2 (Top Right) -->
    <ellipse cx="40" cy="29" rx="5" ry="3" fill="url(#tx_pp_pecan)" stroke="#291002" stroke-width="0.8" transform="rotate(20 40 29)"/>
    <path d="M36 29 Q 40 28 44 29" stroke="#b45309" stroke-width="0.8"/>
    <!-- Pecan 3 (Center) -->
    <ellipse cx="32" cy="35" rx="5.5" ry="3.2" fill="url(#tx_pp_pecan)" stroke="#291002" stroke-width="0.8"/>
    <path d="M28 35 Q 32 34 36 35" stroke="#b45309" stroke-width="0.8"/>
    <!-- Pecan 4 (Lower Center) -->
    <ellipse cx="32" cy="44" rx="4.5" ry="2.6" fill="url(#tx_pp_pecan)" stroke="#291002" stroke-width="0.8" transform="rotate(10 32 44)"/>
    <!-- Caramel Gloss Specular Highlights -->
    <ellipse cx="30" cy="33" rx="1.5" ry="0.6" fill="rgba(255,255,255,0.5)"/>
    <ellipse cx="38" cy="28" rx="1.5" ry="0.6" fill="rgba(255,255,255,0.5)"/>
    <ellipse cx="22" cy="28" rx="1.5" ry="0.6" fill="rgba(255,255,255,0.5)"/>
  </svg>`,

  /**
   * 10. Authentic Cuban Sandwich (food_fl_cuban_sandwich)
   * Pressed Cuban artisan bread with diagonal grill sear marks, distinct layered fillings:
   * mojo roast pork, sweet pink ham, melted swiss cheese, bright yellow mustard line, and dill pickle.
   */
  food_fl_cuban_sandwich: `<svg viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
    <defs>
      <linearGradient id="fl_cb_bread" x1="10" y1="16" x2="54" y2="48" gradientUnits="userSpaceOnUse">
        <stop offset="0%" stop-color="#fef3c7"/>
        <stop offset="60%" stop-color="#fde68a"/>
        <stop offset="100%" stop-color="#d97706"/>
      </linearGradient>
    </defs>
    <!-- Base Shadow -->
    <ellipse cx="32" cy="54" rx="26" ry="6" fill="rgba(0,0,0,0.3)"/>
    <!-- Bottom Pressed Bread Crust -->
    <path d="M8 44 C 8 40, 56 40, 56 44 C 56 48, 8 48, 8 44 Z" fill="#b45309" stroke="#78350f" stroke-width="1"/>
    <!-- Layer 1: Mojo Roast Pork -->
    <rect x="9" y="39" width="46" height="4" rx="1.5" fill="#78350f"/>
    <!-- Layer 2: Sweet Ham -->
    <rect x="9" y="36" width="46" height="3.5" rx="1" fill="#f472b6" stroke="#db2777" stroke-width="0.5"/>
    <!-- Layer 3: Melted Swiss Cheese (dripping) -->
    <path d="M8 33 L 56 33 L 54 36 L 46 38 L 40 35 L 28 38 L 22 35 L 14 38 L 8 36 Z" fill="#fef08a"/>
    <!-- Layer 4: Dill Pickle Slices -->
    <ellipse cx="20" cy="32" rx="6" ry="2.2" fill="#65a30d" stroke="#3f6212" stroke-width="0.6"/>
    <ellipse cx="34" cy="32" rx="6" ry="2.2" fill="#65a30d" stroke="#3f6212" stroke-width="0.6"/>
    <ellipse cx="46" cy="32" rx="6" ry="2.2" fill="#65a30d" stroke="#3f6212" stroke-width="0.6"/>
    <!-- Yellow Mustard Stripe -->
    <path d="M10 30 Q 32 32 54 30" stroke="#facc15" stroke-width="2" stroke-linecap="round"/>
    <!-- Top Pressed Bread Crust -->
    <path d="M8 24 C 8 18, 56 18, 56 24 L 56 28 C 56 30, 8 30, 8 28 Z" fill="url(#fl_cb_bread)" stroke="#92400e" stroke-width="1.2"/>
    <!-- Diagonal Planing Panini Press Grill Lines -->
    <line x1="16" y1="19" x2="26" y2="28" stroke="#78350f" stroke-width="2" stroke-linecap="round"/>
    <line x1="28" y1="19" x2="38" y2="28" stroke="#78350f" stroke-width="2" stroke-linecap="round"/>
    <line x1="40" y1="19" x2="50" y2="28" stroke="#78350f" stroke-width="2" stroke-linecap="round"/>
  </svg>`,

  /**
   * 11. Key West Key Lime Pie (food_fl_key_lime_pie)
   * Pale pastel lime custard pie wedge, dark buttery graham cracker crust, piped whipped cream
   * rosettes along the edge, and a translucent key lime wheel garnish with visible citrus segments.
   */
  food_fl_key_lime_pie: `<svg viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
    <defs>
      <linearGradient id="fl_kl_crust" x1="12" y1="20" x2="52" y2="52" gradientUnits="userSpaceOnUse">
        <stop offset="0%" stop-color="#d97706"/>
        <stop offset="100%" stop-color="#78350f"/>
      </linearGradient>
      <linearGradient id="fl_kl_custard" x1="16" y1="22" x2="48" y2="44" gradientUnits="userSpaceOnUse">
        <stop offset="0%" stop-color="#ecfccb"/>
        <stop offset="60%" stop-color="#d9f99d"/>
        <stop offset="100%" stop-color="#bef264"/>
      </linearGradient>
      <radialGradient id="fl_kl_lime" cx="34" cy="24" r="8" gradientUnits="userSpaceOnUse">
        <stop offset="0%" stop-color="#bef264"/>
        <stop offset="70%" stop-color="#84cc16"/>
        <stop offset="100%" stop-color="#4d7c0f"/>
      </radialGradient>
    </defs>
    <!-- Plate Shadow -->
    <ellipse cx="32" cy="54" rx="26" ry="6" fill="rgba(0,0,0,0.3)"/>
    <ellipse cx="32" cy="52" rx="25" ry="5.5" fill="#1e293b" stroke="rgba(255,255,255,0.12)" stroke-width="1"/>
    <!-- Graham Cracker Crust Base & Back Edge -->
    <path d="M12 24 L 52 24 L 34 54 C 33 55, 31 55, 30 54 Z" fill="url(#fl_kl_crust)" stroke="#451a03" stroke-width="1.2"/>
    <!-- Custard Body -->
    <path d="M14 25 L 50 25 L 33 52 C 32 53, 31 53, 31 52 Z" fill="url(#fl_kl_custard)"/>
    <!-- Piped Whipped Cream Rosettes along Crust Ridge -->
    <circle cx="16" cy="23" r="3.2" fill="#ffffff" stroke="#f1f5f9" stroke-width="0.6"/>
    <circle cx="23" cy="22" r="3.2" fill="#ffffff" stroke="#f1f5f9" stroke-width="0.6"/>
    <circle cx="30" cy="22" r="3.2" fill="#ffffff" stroke="#f1f5f9" stroke-width="0.6"/>
    <circle cx="37" cy="22" r="3.2" fill="#ffffff" stroke="#f1f5f9" stroke-width="0.6"/>
    <circle cx="44" cy="22" r="3.2" fill="#ffffff" stroke="#f1f5f9" stroke-width="0.6"/>
    <circle cx="50" cy="23" r="3.2" fill="#ffffff" stroke="#f1f5f9" stroke-width="0.6"/>
    <!-- Translucent Key Lime Wheel Garnish -->
    <circle cx="34" cy="34" r="8" fill="url(#fl_kl_lime)" stroke="#3f6212" stroke-width="1"/>
    <circle cx="34" cy="34" r="6.8" fill="#d9f99d"/>
    <!-- Citrus Segment Wedges -->
    <path d="M34 34 L 34 28 M 34 34 L 39 30 M 34 34 L 40 35 M 34 34 L 36 40 M 34 34 L 30 39 M 34 34 L 28 34 M 34 34 L 29 29" stroke="#65a30d" stroke-width="0.8"/>
    <circle cx="34" cy="34" r="1.5" fill="#f7fee7"/>
    <!-- Lime Zest Flecks on Cream -->
    <circle cx="22" cy="22" r="0.6" fill="#65a30d"/>
    <circle cx="36" cy="21" r="0.6" fill="#65a30d"/>
    <circle cx="46" cy="22" r="0.6" fill="#65a30d"/>
  </svg>`,

  /**
   * 12. Freshly Squeezed Sunshine OJ (food_fl_orange_juice)
   * Tall faceted glass filled with glowing pulpy orange juice, floating ice cubes with refractive
   * highlights, fresh orange half/wheel on rim with glossy green leaf, cool glass droplets.
   */
  food_fl_orange_juice: `<svg viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
    <defs>
      <linearGradient id="fl_oj_juice" x1="18" y1="20" x2="46" y2="56" gradientUnits="userSpaceOnUse">
        <stop offset="0%" stop-color="#fde047"/>
        <stop offset="30%" stop-color="#fb923c"/>
        <stop offset="80%" stop-color="#f97316"/>
        <stop offset="100%" stop-color="#ea580c"/>
      </linearGradient>
      <radialGradient id="fl_oj_wheel" cx="22" cy="16" r="10" gradientUnits="userSpaceOnUse">
        <stop offset="0%" stop-color="#fdba74"/>
        <stop offset="70%" stop-color="#f97316"/>
        <stop offset="100%" stop-color="#c2410c"/>
      </radialGradient>
    </defs>
    <!-- Glass Base Shadow -->
    <ellipse cx="32" cy="56" rx="18" ry="5" fill="rgba(0,0,0,0.3)"/>
    <!-- Glass Tumbler Back & Orange Juice Fill -->
    <path d="M20 20 L 24 52 C 24 54, 40 54, 40 52 L 44 20 Z" fill="url(#fl_oj_juice)" stroke="rgba(255,255,255,0.4)" stroke-width="1.2"/>
    <!-- Floating Ice Cubes -->
    <rect x="25" y="24" width="8" height="8" rx="2" fill="rgba(255,255,255,0.4)" stroke="rgba(255,255,255,0.8)" stroke-width="0.8" transform="rotate(15 25 24)"/>
    <rect x="33" y="30" width="7" height="7" rx="1.5" fill="rgba(255,255,255,0.3)" stroke="rgba(255,255,255,0.7)" stroke-width="0.8" transform="rotate(-10 33 30)"/>
    <!-- Glass Specular Highlight -->
    <path d="M22 22 L 25 50" stroke="rgba(255,255,255,0.6)" stroke-width="1.8" stroke-linecap="round"/>
    <path d="M42 22 L 39 50" stroke="rgba(255,255,255,0.3)" stroke-width="1.2" stroke-linecap="round"/>
    <!-- Cold Condensation Droplets -->
    <circle cx="28" cy="40" r="1" fill="rgba(255,255,255,0.7)"/>
    <circle cx="36" cy="44" r="0.9" fill="rgba(255,255,255,0.7)"/>
    <circle cx="30" cy="48" r="0.8" fill="rgba(255,255,255,0.6)"/>
    <!-- Fresh Orange Wheel on Rim -->
    <circle cx="20" cy="16" r="9" fill="url(#fl_oj_wheel)" stroke="#9a3412" stroke-width="1"/>
    <circle cx="20" cy="16" r="7.5" fill="#fed7aa"/>
    <path d="M20 16 L 20 9 M 20 16 L 26 12 M 20 16 L 27 18 M 20 16 L 23 23 M 20 16 L 16 23 M 20 16 L 13 18 M 20 16 L 14 12" stroke="#ea580c" stroke-width="0.9"/>
    <!-- Glossy Green Orange Leaf -->
    <path d="M24 10 C 24 4, 34 6, 32 12 C 28 14, 24 12, 24 10 Z" fill="#22c55e" stroke="#15803d" stroke-width="0.6"/>
  </svg>`,

  /**
   * 13. Seattle Double Espresso (food_wa_espresso)
   * Matte dark charcoal porcelain demitasse cup and saucer, rich hazelnut-gold crema swirl,
   * aromatic steam wisps, and a roasted dark coffee bean beside the cup.
   */
  food_wa_espresso: `<svg viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
    <defs>
      <linearGradient id="wa_es_cup" x1="16" y1="20" x2="48" y2="48" gradientUnits="userSpaceOnUse">
        <stop offset="0%" stop-color="#334155"/>
        <stop offset="60%" stop-color="#1e293b"/>
        <stop offset="100%" stop-color="#0f172a"/>
      </linearGradient>
      <radialGradient id="wa_es_crema" cx="30" cy="29" r="14" gradientUnits="userSpaceOnUse">
        <stop offset="0%" stop-color="#fde047"/>
        <stop offset="40%" stop-color="#d97706"/>
        <stop offset="80%" stop-color="#92400e"/>
        <stop offset="100%" stop-color="#451a03"/>
      </radialGradient>
    </defs>
    <!-- Saucer Shadow -->
    <ellipse cx="32" cy="54" rx="26" ry="6" fill="rgba(0,0,0,0.4)"/>
    <!-- Saucer Plate -->
    <ellipse cx="32" cy="50" rx="25" ry="6" fill="url(#wa_es_cup)" stroke="rgba(255,255,255,0.15)" stroke-width="1.2"/>
    <ellipse cx="32" cy="49" rx="17" ry="4" fill="#0f172a"/>
    <!-- Demitasse Cup Handle -->
    <path d="M42 30 C 50 30, 50 42, 40 44" stroke="url(#wa_es_cup)" stroke-width="3.5" stroke-linecap="round" fill="none"/>
    <!-- Demitasse Cup Body -->
    <path d="M16 26 C 16 44, 46 44, 46 26 Z" fill="url(#wa_es_cup)" stroke="rgba(255,255,255,0.12)" stroke-width="1"/>
    <!-- Gold Rim Accent -->
    <ellipse cx="31" cy="26" rx="15" ry="5" fill="#0f172a" stroke="#d97706" stroke-width="0.8"/>
    <!-- Hazelnut Golden Crema -->
    <ellipse cx="31" cy="27" rx="13.5" ry="4.2" fill="url(#wa_es_crema)"/>
    <!-- Crema Swirl Marbling -->
    <path d="M22 27 Q 28 25 34 27 Q 40 28 42 26" stroke="#fef08a" stroke-width="1.2" stroke-linecap="round" fill="none"/>
    <!-- Roasted Coffee Bean beside cup -->
    <ellipse cx="50" cy="50" rx="4" ry="2.5" fill="#451a03" stroke="#1c0a00" stroke-width="0.8" transform="rotate(-20 50 50)"/>
    <path d="M48 50 Q 50 49 52 50" stroke="#78350f" stroke-width="0.8"/>
    <!-- Delicate Steam Wisps -->
    <path d="M26 18 C 24 13, 28 10, 26 4" stroke="rgba(255,255,255,0.45)" stroke-width="1.2" stroke-linecap="round" fill="none"/>
    <path d="M34 19 C 32 14, 37 11, 35 5" stroke="rgba(255,255,255,0.5)" stroke-width="1.2" stroke-linecap="round" fill="none"/>
  </svg>`,

  /**
   * 14. Cedar Plank King Salmon (food_wa_cedar_salmon)
   * Charred rustic cedar wood plank, glistening wild Pacific king salmon fillet with charred grill
   * crosshatch marks, golden lemon slice with pulp detail, and fresh feathery dill herb sprig.
   */
  food_wa_cedar_salmon: `<svg viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
    <defs>
      <linearGradient id="wa_sl_wood" x1="8" y1="14" x2="56" y2="52" gradientUnits="userSpaceOnUse">
        <stop offset="0%" stop-color="#92400e"/>
        <stop offset="50%" stop-color="#78350f"/>
        <stop offset="100%" stop-color="#451a03"/>
      </linearGradient>
      <linearGradient id="wa_sl_meat" x1="16" y1="20" x2="48" y2="44" gradientUnits="userSpaceOnUse">
        <stop offset="0%" stop-color="#fb7185"/>
        <stop offset="50%" stop-color="#f43f5e"/>
        <stop offset="100%" stop-color="#e11d48"/>
      </linearGradient>
    </defs>
    <!-- Cedar Plank Shadow -->
    <rect x="8" y="18" width="48" height="34" rx="4" fill="rgba(0,0,0,0.35)"/>
    <!-- Charred Cedar Wood Plank -->
    <rect x="8" y="16" width="48" height="34" rx="4" fill="url(#wa_sl_wood)" stroke="#291002" stroke-width="1.2"/>
    <!-- Charred Plank Edges (Blackened bark) -->
    <line x1="8" y1="16" x2="56" y2="16" stroke="#18181b" stroke-width="1.8"/>
    <line x1="8" y1="50" x2="56" y2="50" stroke="#18181b" stroke-width="1.8"/>
    <!-- Wild Pacific King Salmon Fillet -->
    <path d="M14 26 C 14 22, 42 20, 50 26 C 50 36, 42 42, 14 38 Z" fill="url(#wa_sl_meat)" stroke="#be123c" stroke-width="1"/>
    <!-- Charred Diamond Grill Crosshatching -->
    <line x1="20" y1="24" x2="28" y2="38" stroke="#450a0a" stroke-width="1.5" stroke-linecap="round"/>
    <line x1="28" y1="23" x2="36" y2="39" stroke="#450a0a" stroke-width="1.5" stroke-linecap="round"/>
    <line x1="36" y1="23" x2="44" y2="38" stroke="#450a0a" stroke-width="1.5" stroke-linecap="round"/>
    <line x1="26" y1="38" x2="34" y2="24" stroke="#450a0a" stroke-width="1.5" stroke-linecap="round"/>
    <line x1="34" y1="39" x2="42" y2="24" stroke="#450a0a" stroke-width="1.5" stroke-linecap="round"/>
    <!-- Salmon Flake Segments & Gloss -->
    <path d="M18 30 Q 30 28 44 32" stroke="rgba(255,255,255,0.4)" stroke-width="1" stroke-linecap="round"/>
    <!-- Lemon Slice on Top -->
    <ellipse cx="26" cy="28" rx="6" ry="4.5" fill="#facc15" stroke="#ca8a04" stroke-width="0.8" transform="rotate(-15 26 28)"/>
    <ellipse cx="26" cy="28" rx="4.5" ry="3.2" fill="#fef08a" transform="rotate(-15 26 28)"/>
    <!-- Fresh Dill Herb Sprig -->
    <path d="M38 32 Q 44 28 46 25" stroke="#22c55e" stroke-width="1" stroke-linecap="round" fill="none"/>
    <path d="M42 30 L 41 27 M 44 28 L 45 26 M 45 31 L 47 30" stroke="#16a34a" stroke-width="0.8"/>
  </svg>`,

  /**
   * 15. Rainier Cherry Tart (food_wa_cherry_tart)
   * Fluted golden pastry tart shell, rich vanilla pastry cream bed, two glossy Rainier cherries
   * (yellow blush with rose-red gradients), slender green stems, and glistening apricot fruit glaze.
   */
  food_wa_cherry_tart: `<svg viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
    <defs>
      <linearGradient id="wa_ct_crust" x1="12" y1="24" x2="52" y2="52" gradientUnits="userSpaceOnUse">
        <stop offset="0%" stop-color="#fbbf24"/>
        <stop offset="60%" stop-color="#d97706"/>
        <stop offset="100%" stop-color="#92400e"/>
      </linearGradient>
      <radialGradient id="wa_ct_cherry1" cx="26" cy="26" r="8" gradientUnits="userSpaceOnUse">
        <stop offset="0%" stop-color="#fef08a"/>
        <stop offset="35%" stop-color="#f43f5e"/>
        <stop offset="80%" stop-color="#e11d48"/>
        <stop offset="100%" stop-color="#9f1239"/>
      </radialGradient>
      <radialGradient id="wa_ct_cherry2" cx="38" cy="28" r="8" gradientUnits="userSpaceOnUse">
        <stop offset="0%" stop-color="#fef08a"/>
        <stop offset="35%" stop-color="#f43f5e"/>
        <stop offset="80%" stop-color="#e11d48"/>
        <stop offset="100%" stop-color="#9f1239"/>
      </radialGradient>
    </defs>
    <!-- Base Plate Shadow -->
    <ellipse cx="32" cy="54" rx="26" ry="6" fill="rgba(0,0,0,0.3)"/>
    <ellipse cx="32" cy="52" rx="25" ry="5.5" fill="#1e293b" stroke="rgba(255,255,255,0.12)" stroke-width="1"/>
    <!-- Fluted Tart Shell Base -->
    <path d="M12 36 L 16 50 C 16 52, 48 52, 48 50 L 52 36 Z" fill="url(#wa_ct_crust)" stroke="#78350f" stroke-width="1.2"/>
    <!-- Fluted Pastry Rim -->
    <path d="M10 36 C 14 33, 18 33, 22 36 C 26 33, 30 33, 34 36 C 38 33, 42 33, 46 36 C 50 33, 54 33, 54 36 C 54 40, 10 40, 10 36 Z" fill="#f59e0b" stroke="#b45309" stroke-width="1"/>
    <!-- Vanilla Custard Cream Layer -->
    <ellipse cx="32" cy="37" rx="18" ry="4.5" fill="#fef9c3"/>
    <!-- Rainier Cherry 1 (Left - Golden-Yellow Blush with Ruby Gradient) -->
    <circle cx="26" cy="30" r="7.5" fill="url(#wa_ct_cherry1)" stroke="#881337" stroke-width="0.8"/>
    <!-- Specular Highlight Curve -->
    <ellipse cx="24" cy="27" rx="2" ry="1" fill="rgba(255,255,255,0.7)" transform="rotate(-30 24 27)"/>
    <!-- Rainier Cherry 2 (Right) -->
    <circle cx="38" cy="32" r="7.5" fill="url(#wa_ct_cherry2)" stroke="#881337" stroke-width="0.8"/>
    <ellipse cx="36" cy="29" rx="2" ry="1" fill="rgba(255,255,255,0.7)" transform="rotate(-30 36 29)"/>
    <!-- Slender Green Cherry Stems joining at top -->
    <path d="M26 23 C 24 16, 28 8, 32 6" stroke="#65a30d" stroke-width="1.4" stroke-linecap="round" fill="none"/>
    <path d="M38 25 C 38 17, 34 8, 32 6" stroke="#65a30d" stroke-width="1.4" stroke-linecap="round" fill="none"/>
    <!-- Mint Leaf at Stem Joint -->
    <path d="M32 6 C 36 4, 40 7, 38 10 C 35 10, 33 8, 32 6 Z" fill="#22c55e" stroke="#15803d" stroke-width="0.6"/>
  </svg>`,
};
